import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MandateMatch } from '../../types';
import {
  useMandateMatches,
  useMandateSuggestions,
  useAddMandateMatch,
  useUpdateMandateMatch,
  useRemoveMandateMatch,
} from '../../hooks/useMandateMatches';
import { InvestorDetailModal } from './InvestorDetailModal';
import { MandateMatchOverrideModal } from './MandateMatchOverrideModal';

const STATUS_OPTIONS = [
  { value: 'neuer_match', label: 'Neuer Match' },
  { value: 'angefragt', label: 'Angefragt' },
  { value: 'interessiert', label: 'Interessiert' },
  { value: 'in_verhandlung', label: 'In Verhandlung' },
  { value: 'keine_interesse', label: 'Kein Interesse' },
  { value: 'abgeschlossen', label: 'Abgeschlossen' },
];

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  neuer_match: { bg: '#e8eaf6', color: '#3949ab' },
  angefragt: { bg: 'var(--surface2)', color: 'var(--text3)' },
  interessiert: { bg: '#e8f5e9', color: '#388e3c' },
  keine_interesse: { bg: '#fce4ec', color: '#c62828' },
  in_verhandlung: { bg: '#fff3e0', color: '#e65100' },
  abgeschlossen: { bg: 'var(--accent-light)', color: 'var(--accent)' },
};

const COUNTRY_NAMES: Record<string, string> = {
  DE: 'DE', AT: 'AT', CH: 'CH', ES: 'ES', FR: 'FR', IT: 'IT',
  NL: 'NL', GB: 'GB', PL: 'PL', PT: 'PT', LU: 'LU', US: 'US',
};

interface Props {
  mandateId: string;
  mandateTitle?: string;
  onSent?: () => void;
}

export function MandateMatchTable({ mandateId, mandateTitle = '', onSent }: Props) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const qc = useQueryClient();
  const [detailCompanyId, setDetailCompanyId] = useState<string | null>(null);
  const [detailMatchProfileId, setDetailMatchProfileId] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState<string | null>(null);
  const [notesValue, setNotesValue] = useState('');

  const openDetail = (companyId: string, profileId: string | null | undefined) => {
    setDetailCompanyId(companyId);
    setDetailMatchProfileId(profileId ?? null);
  };

  // Deep-Link: ?openInvestor=<companyId> → Zeile highlighten + scrollen (KEIN Modal)
  const highlightedCompanyId = searchParams.get('openInvestor');
  const rowRefs = React.useRef<Map<string, HTMLTableRowElement>>(new Map());

  const closeDetail = () => {
    setDetailCompanyId(null);
    setDetailMatchProfileId(null);
  };

  const { data: matches = [], isLoading } = useMandateMatches(mandateId);
  const { data: suggestionsData, isLoading: loadingSuggestions } = useMandateSuggestions(mandateId, showSuggestions);

  // Erst scrollen, wenn Matches geladen sind und die Zeile im DOM existiert.
  React.useEffect(() => {
    if (!highlightedCompanyId || isLoading) return;
    const match = matches.find(m => m.company_id === highlightedCompanyId);
    if (!match) return; // nicht als Match vorhanden → nichts zu scrollen

    const timer = setTimeout(() => {
      const row = rowRefs.current.get(highlightedCompanyId);
      if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    return () => clearTimeout(timer);
  }, [highlightedCompanyId, isLoading, matches]);
  const suggestions = suggestionsData?.data ?? [];

  const { mutate: addMatch } = useAddMandateMatch(mandateId);
  const { mutate: updateMatch } = useUpdateMandateMatch(mandateId);
  const { mutate: removeMatch } = useRemoveMandateMatch(mandateId);

  const addedCompanyIds = new Set(matches.map(m => m.company_id));

  const handleStatusChange = (match: MandateMatch, status: string) => {
    updateMatch({ id: match.id, payload: { status } });
  };

  const handleNotesEdit = (match: MandateMatch) => {
    setEditingNotes(match.id);
    setNotesValue(match.notes ?? '');
  };

  const handleNotesSave = (matchId: string) => {
    updateMatch({ id: matchId, payload: { notes: notesValue || null } });
    setEditingNotes(null);
  };

  const handleAddSuggestion = (s: MandateMatch) => {
    addMatch({
      mandate_id: mandateId,
      company_id: s.company_id,
      profile_id: s.profile_id,
      status: 'neuer_match',
      match_score: s.match_score,
    });
  };

  const thStyle: React.CSSProperties = {
    padding: '9px 12px', textAlign: 'left', fontWeight: 500,
    fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text3)',
    background: 'var(--surface2)',
  };
  const tdStyle: React.CSSProperties = { padding: '10px 12px', fontSize: '13px', verticalAlign: 'middle' };

  return (
    <div>
      {/* Tracked matches */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ fontSize: '13px', color: 'var(--text2)' }}>
          {matches.length} Investor{matches.length !== 1 ? 'en' : ''} erfasst
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setShowOverrideModal(true)}
            title="Such-Einstellungen für dieses Mandat überschreiben"
            style={{
              padding: '7px 12px', fontSize: '13px',
              background: 'var(--surface)', color: 'var(--text2)',
              border: '1px solid var(--border2)',
              borderRadius: 'var(--radius)', cursor: 'pointer',
            }}
          >
            ⚙ Such-Einstellungen
          </button>
          <button
            onClick={() => setShowSuggestions(v => !v)}
            style={{
              padding: '7px 14px', fontSize: '13px', fontWeight: 500,
              background: showSuggestions ? 'var(--surface2)' : 'var(--accent)',
              color: showSuggestions ? 'var(--text2)' : '#fff',
              border: showSuggestions ? '1px solid var(--border2)' : 'none',
              borderRadius: 'var(--radius)', cursor: 'pointer',
            }}
          >
            {showSuggestions ? 'Vorschläge ausblenden' : 'Passende Investoren finden'}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text3)' }}>Laden...</div>
      ) : matches.length > 0 ? (
        <div className="card" style={{ overflow: 'hidden', marginBottom: '20px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Investor</th>
                <th style={thStyle}>Profil</th>
                <th style={thStyle}>Standort</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Score</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Notizen</th>
                <th style={{ ...thStyle, textAlign: 'center' }}></th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m, idx) => {
                const sc = STATUS_COLORS[m.status] ?? STATUS_COLORS.angefragt;
                const isHighlighted = highlightedCompanyId === m.company_id;
                return (
                  <tr key={m.id}
                    ref={el => { if (el) rowRefs.current.set(m.company_id, el); else rowRefs.current.delete(m.company_id); }}
                    style={{
                      borderTop: idx > 0 ? '1px solid var(--border)' : undefined,
                      background: isHighlighted ? 'var(--accent-light)' : undefined,
                      boxShadow: isHighlighted ? 'inset 3px 0 0 var(--accent)' : undefined,
                      transition: 'background 0.3s',
                    }}>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{ fontWeight: 500, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                          onClick={() => openDetail(m.company_id, m.profile_id)}
                          title="Investordetails & Exposé versenden"
                        >
                          {m.company_name}
                        </div>
                        <span
                          title="In neuem Tab öffnen"
                          style={{ fontSize: '11px', color: 'var(--text3)', cursor: 'pointer' }}
                          onClick={e => { e.stopPropagation(); window.open(`/companies/${m.company_id}`, '_blank'); }}
                        >
                          ↗
                        </span>
                      </div>
                      {m.company_type && <div style={{ fontSize: '11px', color: 'var(--text3)' }}>{m.company_type}</div>}
                    </td>
                    <td style={{ ...tdStyle, fontSize: '12px', color: 'var(--text2)' }}>
                      {m.profile_name
                        ? <span
                            style={{ cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px', color: 'var(--text)' }}
                            onClick={e => {
                              e.stopPropagation();
                              if (m.profile_id) {
                                window.open(`/companies/${m.company_id}?openProfile=${m.profile_id}`, '_blank');
                              } else {
                                openDetail(m.company_id, m.profile_id);
                              }
                            }}
                            title="Ankaufsprofil öffnen"
                          >
                            {m.profile_name}
                          </span>
                        : '—'}
                    </td>
                    <td style={{ ...tdStyle, fontSize: '12px', color: 'var(--text2)' }}>
                      {[m.city, m.country_code ? COUNTRY_NAMES[m.country_code] ?? m.country_code : null].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: '20px',
                        fontSize: '12px', fontWeight: 600,
                        background: m.match_score >= 70 ? 'var(--accent-light)' : 'var(--surface2)',
                        color: m.match_score >= 70 ? 'var(--accent)' : 'var(--text3)',
                        fontFamily: 'DM Mono, monospace',
                      }}>
                        {m.match_score}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <select
                        value={m.status}
                        onChange={e => handleStatusChange(m, e.target.value)}
                        style={{
                          padding: '3px 8px', borderRadius: '20px', fontSize: '12px',
                          border: 'none', cursor: 'pointer', fontWeight: 500,
                          background: sc.bg, color: sc.color, outline: 'none',
                        }}
                      >
                        {STATUS_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ ...tdStyle, maxWidth: '180px' }}>
                      {editingNotes === m.id ? (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input
                            autoFocus
                            value={notesValue}
                            onChange={e => setNotesValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleNotesSave(m.id); if (e.key === 'Escape') setEditingNotes(null); }}
                            style={{ flex: 1, padding: '3px 8px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '12px', background: 'var(--surface)', color: 'var(--text)' }}
                          />
                          <button type="button" onClick={() => handleNotesSave(m.id)}
                            style={{ padding: '3px 8px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '12px', cursor: 'pointer' }}>
                            ✓
                          </button>
                        </div>
                      ) : (
                        <span
                          onClick={() => handleNotesEdit(m)}
                          style={{ cursor: 'text', color: m.notes ? 'var(--text2)' : 'var(--text3)', fontSize: '12px', fontStyle: m.notes ? 'normal' : 'italic' }}
                          title="Klicken zum Bearbeiten"
                        >
                          {m.notes ?? 'Notiz hinzufügen...'}
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => removeMatch(m.id)}
                        title="Entfernen"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: '16px', lineHeight: 1 }}
                        onMouseEnter={e => (e.currentTarget.style.color = 'var(--accent)')}
                        onMouseLeave={e => (e.currentTarget.style.color = 'var(--text3)')}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text3)', fontSize: '13px', marginBottom: '20px' }}>
          Noch keine Investoren erfasst. Nutze "Passende Investoren finden" um Vorschläge zu sehen.
        </div>
      )}

      {/* Suggestions panel — farblich abgehoben für intuitive Sichtbarkeit */}
      {showSuggestions && (
        <div style={{
          background: 'var(--accent-light)',
          border: '2px solid var(--accent-mid)',
          borderRadius: 'var(--radius)',
          padding: '14px 16px',
          marginTop: '8px',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
            paddingBottom: 10, borderBottom: '1px solid var(--accent-mid)',
          }}>
            <span style={{
              padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
              background: 'var(--accent)', color: '#fff', letterSpacing: '0.4px',
            }}>🎯 VORSCHLÄGE</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--accent)' }}>
              Passende Investoren — basierend auf Anforderungsprofilen
            </span>
            {suggestions.length > 0 && (
              <span style={{ fontSize: 12, color: 'var(--accent)', marginLeft: 'auto', fontWeight: 600 }}>
                {suggestions.length} Treffer
              </span>
            )}
          </div>
          {loadingSuggestions ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--accent)', fontWeight: 500 }}>Analyse läuft...</div>
          ) : suggestions.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text3)', fontSize: '13px' }}>
              Keine passenden Investorenprofile gefunden.
            </div>
          ) : (
            <div className="card" style={{ overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Investor</th>
                    <th style={thStyle}>Profil</th>
                    <th style={thStyle}>Standort</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Score</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.map((s, idx) => {
                    const alreadyAdded = addedCompanyIds.has(s.company_id);
                    return (
                      <tr key={s.company_id} style={{ borderTop: idx > 0 ? '1px solid var(--border)' : undefined, opacity: alreadyAdded ? 0.5 : 1 }}>
                        <td style={tdStyle}>
                          <div
                            style={{ fontWeight: 500, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                            onClick={() => openDetail(s.company_id, s.profile_id)}
                            title="Investordetails öffnen"
                          >
                            {s.company_name}
                          </div>
                          {s.company_type && <div style={{ fontSize: '11px', color: 'var(--text3)' }}>{s.company_type}</div>}
                        </td>
                        <td style={{ ...tdStyle, fontSize: '12px', color: 'var(--text2)' }}>
                          {s.profile_name
                            ? <span
                                style={{ cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px', color: 'var(--text)' }}
                                onClick={e => {
                                  e.stopPropagation();
                                  if (s.profile_id) {
                                    window.open(`/companies/${s.company_id}?openProfile=${s.profile_id}`, '_blank');
                                  } else {
                                    openDetail(s.company_id, s.profile_id);
                                  }
                                }}
                                title="Ankaufsprofil öffnen"
                              >
                                {s.profile_name}
                              </span>
                            : '—'}
                        </td>
                        <td style={{ ...tdStyle, fontSize: '12px', color: 'var(--text2)' }}>
                          {[s.city, s.country_code ? COUNTRY_NAMES[s.country_code] ?? s.country_code : null].filter(Boolean).join(', ') || '—'}
                          {s.match_reasons && s.match_reasons.length > 0 && (
                            <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 4 }}>
                              ⚠ {s.match_reasons.join(' · ')}
                            </div>
                          )}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <span
                            title={s.score_breakdown ? `Asset ${s.score_breakdown.asset} · Lage ${s.score_breakdown.location} · Finanz ${s.score_breakdown.financial}` : undefined}
                            style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: '20px',
                              fontSize: '12px', fontWeight: 600,
                              background: s.match_score >= 70 ? 'var(--accent-light)' : s.match_score >= 40 ? 'var(--warn-bg)' : 'var(--surface2)',
                              color: s.match_score >= 70 ? 'var(--accent)' : s.match_score >= 40 ? 'var(--warn)' : 'var(--text3)',
                              fontFamily: 'DM Mono, monospace',
                            }}
                          >
                            {s.match_score}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {alreadyAdded ? (
                            <span style={{ fontSize: '11px', color: 'var(--text3)' }}>Bereits erfasst</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleAddSuggestion(s)}
                              style={{
                                padding: '4px 12px', fontSize: '12px', fontWeight: 500,
                                background: 'var(--accent)', color: '#fff',
                                border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer',
                              }}
                            >
                              + Hinzufügen
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Investor-Detail-Modal mit Exposé-Versand */}
      <InvestorDetailModal
        companyId={detailCompanyId}
        matchProfileId={detailMatchProfileId}
        mandateId={mandateId}
        mandateTitle={mandateTitle}
        initialTab={searchParams.get('openTab') === 'communication' ? 'communication' : 'overview'}
        onClose={closeDetail}
        onSent={onSent}
      />
      {showOverrideModal && (
        <MandateMatchOverrideModal
          mandateId={mandateId}
          onClose={() => setShowOverrideModal(false)}
          onSaved={() => {
            setShowOverrideModal(false);
            // Vorschläge neu laden (settings haben sich geändert)
            qc.invalidateQueries({ queryKey: ['mandate-suggestions', mandateId] });
          }}
        />
      )}
    </div>
  );
}
